import React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchCustomer } from '../../common/js/bfx-customer-utility';
import { getStripeKey, resolveStripeGateway } from '../../common/js/stripe-utility';
import { axiosGet } from '../../common/js/axios-utility';
import { type RecurringRepairSubscription } from '../types/recurring-repair.types';
import { type WebCustomer2, type WebPaymentMethod2 } from '../../common/types/webshop.types';
import Billing from './Billing';
import SubscriptionSummary from './SubscriptionSummary';
import StripeProvider from '../../common/components/payment-method/StripeProvider';
import LicenseSummary from './LicenseSummary';
import Intro from './Intro';
import SpinnerOverlay from '../../common/components/SpinnerOverlay';

import { errorToast } from '../../common/js/toast-utility';

type Props = {
    subscriptionId: string
}

export default function RecurringRepair({ subscriptionId }: Props) {
    const subscriptionQuery = useQuery({
        queryKey: ['repair', 'subscription'],
        queryFn: fetchSubscription, 
        refetchOnWindowFocus: false 
    });
    const customerQuery = useQuery({
        queryKey: ['repair', 'customer'], 
        queryFn: fetchCustomer, 
        refetchOnWindowFocus: false 
    });

    const queryClient = useQueryClient();

    const [submitting, setSubmitting] = React.useState(false);

    async function fetchSubscription(): Promise<RecurringRepairSubscription> {
        try{
            const { data } = await axiosGet('/subscription/repair/fetch/' + subscriptionId);
            return data;
        }
        catch(e){
            console.error(e);
            errorToast('Could not retrieve subscription');
        }
    }

    async function handleComplete() {
        queryClient.invalidateQueries({queryKey: ['repair', 'subscription']});
    }

    const subscription: RecurringRepairSubscription = subscriptionQuery.data;
    const customer: WebCustomer2 = customerQuery.data;

    const gatewayKey = React.useMemo(() => {
        return customer != null ? getStripeKey(resolveStripeGateway(customer)) : null;
    }, [customer])

    const lics = subscription ? subscription.licenses : [];

    const showSpinner = subscriptionQuery.isFetching || customerQuery.isFetching || !customer || !subscription;

    return (
        <div className='recurring-repair-container'>
            <SpinnerOverlay opaque fullscreen message='Please Wait...' size={125} hidden={!showSpinner} />
            <div className='recurring-repair-content'>
                <h1>Subscription Settings</h1>
                <div className='recurring-repair-intro'>
                    <Intro subscription={subscription} />
                </div>
                <div className='recurring-repair-billing'>
                    <StripeProvider publicKey={gatewayKey}>
                        {subscription && !subscription.paymentMethodId && (
                            <div className='recurring-repair-payment'>
                                <Billing customer={customer} subscription={subscription} onComplete={handleComplete} />
                            </div>
                        )}
                    </StripeProvider>
                    <div className='recurring-repair-summary'>
                        <SubscriptionSummary subscription={subscription} />
                    </div>
                </div>
                <div className='recurring-renewal licenses'>
                    <LicenseSummary sub={subscription} />
                </div>
            </div>
        </div>
    )
}